import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { register, verifyEmail, verifyGroupCode, clearRegistrationData, selfRegister, logout } from '../actions/authActions';
import { getTermsByProgram } from '../actions/termsConditionsActions';
import { clearErrors } from '../actions/errorActions';
import { Target, TrendingUp, Lightbulb, Rocket } from 'lucide-react';
import EmailStep from '../components/auth/registration/EmailStep';
import TermsStep from '../components/auth/registration/TermsStep';
import UserDetailsStep from '../components/auth/registration/UserDetailsStep';

// Module-level guard to prevent duplicate self-register calls (React 18 strict mode)
let selfRegisterInProgress = false;

const RegisterPage = ({
  auth: { isAuthenticated, isLoading: authLoading },
  error,
  registrationData,
  registrationTerms,
  termsFetched,
  register,
  verifyEmail,
  verifyGroupCode,
  getTermsByProgram,
  clearErrors,
  clearRegistrationData,
  selfRegister,
  logout
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramEmail = searchParams.get('email');
  const paramProgramId = searchParams.get('programId');
  const paramReferralCode = searchParams.get('ref');
  const paramPathwayId = searchParams.get('pathwayId');
  const isSelfRegister = !!(paramEmail && (paramProgramId || paramReferralCode));
  const isReferralLink = !!paramReferralCode;
  const isProgramLink = !!(paramProgramId && !paramEmail && !paramReferralCode);
  const IBM_PROGRAM_ID = '6877f534c37408b35b2fb73d';

  // Track whether user was already authenticated when the page loaded
  // null = still waiting for auth to settle, true/false = resolved
  const [wasAuthOnMount, setWasAuthOnMount] = useState(null);
  useEffect(() => {
    if (!authLoading && wasAuthOnMount === null) {
      setWasAuthOnMount(!!isAuthenticated);
    }
  }, [authLoading, isAuthenticated]);

  const [currentStep, setCurrentStep] = useState(1);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');

  // Auto-register when coming from external link (skip registration page entirely)
  const [selfRegisterGracePeriod, setSelfRegisterGracePeriod] = useState(true);
  useEffect(() => {
    if (isSelfRegister && !isAuthenticated && !selfRegisterInProgress) {
      selfRegisterInProgress = true;
      setLoading(true);
      if (paramReferralCode) sessionStorage.setItem('referral_registration', 'true');
      if (paramPathwayId) sessionStorage.setItem('referral_pathwayId', paramPathwayId);
      selfRegister({ email: paramEmail, programId: paramProgramId, referralCode: paramReferralCode, pathwayId: paramPathwayId });
      const timer = setTimeout(() => setSelfRegisterGracePeriod(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isSelfRegister, isAuthenticated]);

  // Redirect if already authenticated — wait for auth to settle first
  const isReferralFlow = isSelfRegister || isReferralLink || isProgramLink;
  useEffect(() => {
    if (authLoading || wasAuthOnMount === null) return;
    if (isAuthenticated) {
      if (isReferralFlow && !wasAuthOnMount) {
        // User just registered through a referral/program flow — always go to claim page
        navigate('/claim-skillsbuild');
      } else if (!isReferralFlow) {
        navigate('/');
      }
      // If isReferralFlow && wasAuthOnMount — they were already logged in, show "already signed in" UI
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Clear stale errors on mount for self-register flow
  useEffect(() => {
    if (isSelfRegister) clearErrors();
    return () => {
      selfRegisterInProgress = false;
      clearErrors();
      clearRegistrationData();
    };
  }, []);

  useEffect(() => {
    if (registrationData.noInvitation) {
      setLoading(false);
      setMsg(null);
    }
  }, [registrationData.noInvitation]);

  useEffect(() => {
    if (error.id === 'REGISTER_FAIL' || error.id === 'VERIFY_EMAIL_FAIL') {
      const raw = typeof error.msg === 'string' ? error.msg : error.msg?.msg || error.msg?.errors;

      // For self-register flow, only suppress the generic server error during grace period
      // Let specific errors like "Email has already been registered" through immediately
      if ((isSelfRegister || isReferralLink) && selfRegisterGracePeriod) {
        const errText = Array.isArray(raw)
          ? raw.map(e => (typeof e === 'object' ? e.msg : e)).join(', ')
          : raw || '';
        if (errText === 'Unable to register your account.') return;
      }
      const m = Array.isArray(raw)
        ? raw.map(e => (typeof e === 'object' ? e.msg : e)).join(', ')
        : raw || 'Operation failed';
      if (!registrationData.noInvitation) {
        setMsg(m);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } else {
      setMsg(null);
    }
  }, [error]);

  // Advance steps when email verified
  useEffect(() => {
    if (registrationData.email && !registrationData.noInvitation && currentStep === 1) {
      if (registrationData.isAdmin) {
        setCurrentStep(3);
      } else if (registrationData.program?._id) {
        getTermsByProgram(registrationData.program._id);
        setCurrentStep(2);
      } else {
        // No program — skip terms step
        setCurrentStep(3);
      }
      setMsg(null);
      setLoading(false);
    }
  }, [registrationData]);

  // Skip terms step if terms were fetched but none exist
  useEffect(() => {
    if (currentStep === 2 && termsFetched && !registrationTerms) {
      setCurrentStep(3);
    }
  }, [currentStep, termsFetched, registrationTerms]);

  const handleEmailVerification = (email) => {
    setMsg(null);
    setLoading(true);
    verifyEmail(email);
  };

  const handleGroupCodeVerification = (email, groupCode) => {
    setMsg(null);
    setLoading(true);
    verifyGroupCode(email, groupCode);
  };

  const handleTermsAccept = () => {
    setCurrentStep(3);
    setMsg(null);
  };

  const handleRegistration = (userData) => {
    setMsg(null);
    setLoading(true);
    const payload = registrationData.viaGroupCode
      ? { ...userData, groupCode: registrationData.group?.code }
      : userData;
    register(payload);
  };

  const handleSelfRegister = () => {
    if (!termsAccepted) {
      setMsg('Please accept the Terms & Conditions to continue.');
      return;
    }
    setMsg(null);
    setLoading(true);
    selfRegister({ email: paramEmail, programId: paramProgramId, referralCode: paramReferralCode, pathwayId: paramPathwayId });
  };

  const handleReferralRegister = (e) => {
    e.preventDefault();
    if (!referralEmail.trim()) return;
    setMsg(null);
    setLoading(true);
    sessionStorage.setItem('referral_registration', 'true');
    if (paramPathwayId) sessionStorage.setItem('referral_pathwayId', paramPathwayId);
    selfRegister({ email: referralEmail.trim(), programId: paramProgramId, referralCode: paramReferralCode, pathwayId: paramPathwayId });
  };

  const handleProgramRegister = (e) => {
    e.preventDefault();
    if (!referralEmail.trim()) return;
    setMsg(null);
    setLoading(true);
    selfRegister({ email: referralEmail.trim(), programId: paramProgramId });
  };

  const handleSkipGroupCode = () => {
    setLoading(false);
    setMsg(null);
    setCurrentStep(3);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setMsg(null);
    setLoading(false);
    clearRegistrationData();
  };

  const handleBackToStep2 = () => {
    if (registrationData.isAdmin) {
      setCurrentStep(1);
      setMsg(null);
      setLoading(false);
    } else {
      setCurrentStep(2);
      setMsg(null);
      setLoading(false);
    }
  };

  const isAdmin = registrationData?.isAdmin;
  const totalSteps = isAdmin ? 2 : 3;

  const getDisplayStep = () => {
    if (isAdmin) return currentStep === 3 ? 2 : 1;
    return currentStep;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <EmailStep
            onVerifyEmail={handleEmailVerification}
            onVerifyGroupCode={handleGroupCodeVerification}
            onSkipGroupCode={handleSkipGroupCode}
            error={msg}
            loading={loading}
            noInvitation={registrationData.noInvitation}
            email={registrationData.email}
          />
        );
      case 2:
        return (
          <TermsStep
            termsConditions={registrationTerms}
            onAccept={handleTermsAccept}
            onBack={handleBackToStep1}
            loading={!registrationTerms}
          />
        );
      case 3:
        return (
          <UserDetailsStep
            onRegister={handleRegistration}
            onBack={handleBackToStep2}
            error={msg}
            loading={loading}
            email={registrationData.email}
          />
        );
      default:
        return null;
    }
  };

  const isIBMProgram = isReferralFlow || paramProgramId === IBM_PROGRAM_ID;

  const features = [
    { icon: Target,     text: 'Set and track your learning goals' },
    { icon: TrendingUp, text: 'Monitor your progress in real time' },
    { icon: Lightbulb,  text: 'Access courses built for your path' },
    { icon: Rocket,     text: 'Achieve more with structured learning' },
  ];

  return (
    <div className="min-h-screen flex">

      {/* Left — Branding Panel */}
      {isIBMProgram ? (
        <div className="hidden lg:flex lg:w-1/2 bg-white flex-col items-center justify-center p-12">
          <div className="max-w-lg w-full text-center space-y-6">
            <h1 className="text-3xl font-extrabold text-[#054581] leading-tight">
              CLAIM YOUR STUDENT PASS<br />
              <span className="text-[#1db5d4]">IBM SKILLSBUILD</span>
            </h1>
            <p className="text-gray-500 text-sm">
              Join the IBM SkillsBuild AI Campus Connect program and get free access to IBM's learning platform.
            </p>
            <img
              src="/images/newimage.png"
              alt="Claim Your Student Pass - IBM SkillsBuild"
              className="w-full rounded-xl shadow-2xl"
            />
          </div>
        </div>
      ) : (
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#054581] to-[#1086a4] flex-col justify-between p-12 text-white">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-1">Your Big Year</h1>
            <p className="text-white/70 text-sm">Learning Management System</p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold leading-snug">
              Start your journey<br />towards your goals.
            </h2>
            <div className="space-y-3 pt-2">
              {features.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-lg p-2 shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm text-white/90">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-white/50 text-xs">powered by Your Big Year®</p>
        </div>
      )}

      {/* Right — Form Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">

          {/* Mobile header */}
          {isIBMProgram ? (
            <div className="lg:hidden text-center mb-8">
              <img
                src="/images/newimage.png"
                alt="Claim Your Student Pass - IBM SkillsBuild"
                className="w-full max-w-xs mx-auto rounded-xl shadow-lg mb-4"
              />
              <h1 className="text-2xl font-extrabold text-[#054581] leading-tight">
                CLAIM YOUR STUDENT PASS<br />
                <span className="text-[#1db5d4]">IBM SKILLSBUILD</span>
              </h1>
            </div>
          ) : (
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-2xl font-bold text-[#054581]">Your Big Year</h1>
              <p className="text-gray-500 text-sm">Learning Management System</p>
            </div>
          )}

          {(authLoading || wasAuthOnMount === null) && isReferralFlow ? (
            <div className="text-center py-8">
              <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-[#1086a4] rounded-full animate-spin mb-4" />
              <h2 className="text-2xl font-bold text-[#054581] mb-2">Loading...</h2>
              <p className="text-gray-500 text-sm">Please wait.</p>
            </div>
          ) : isAuthenticated && isReferralFlow && wasAuthOnMount ? (
            <div className="text-center py-8 space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-2">
                <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[#054581]">You're already signed in</h2>
              <p className="text-gray-500 text-sm">
                Please sign out of your current account first, then open the referral link again to register a new account.
              </p>
              <button
                onClick={logout}
                className="w-full py-3 px-4 bg-[#1086a4] hover:bg-[#0c6d85] text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 transition-colors duration-200 shadow-sm"
              >
                Sign Out
              </button>
              <p className="text-sm text-gray-400">
                After signing out, open the referral link again to create a new account.
              </p>
            </div>
          ) : isSelfRegister ? (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-[#1086a4] rounded-full animate-spin mb-4" />
                  <h2 className="text-2xl font-bold text-[#054581] mb-2">Setting up your account...</h2>
                  <p className="text-gray-500 text-sm">Loading, please wait.</p>
                </div>
              ) : msg ? (
                <>
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {msg}
                  </div>
                  <p className="mt-6 text-center text-sm text-gray-500">
                    Already have an account?{' '}
                    <Link to="/login" className="text-[#1086a4] font-semibold hover:text-[#054581] transition-colors">
                      Sign in
                    </Link>
                  </p>
                </>
              ) : (
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-[#054581] mb-2">Setting up your account...</h2>
                  <p className="text-gray-500 text-sm">Your login password will be sent to your email.</p>
                </div>
              )}
            </>
          ) : isReferralLink ? (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-[#1086a4] rounded-full animate-spin mb-4" />
                  <h2 className="text-2xl font-bold text-[#054581] mb-2">Setting up your account...</h2>
                  <p className="text-gray-500 text-sm">Loading, please wait.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-[#054581] mb-2">You've been referred!</h2>
                    <p className="text-gray-500 text-sm">Enter your email to join. Your login password will be sent to you.</p>
                  </div>

                  {msg && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {msg}
                    </div>
                  )}

                  <form onSubmit={handleReferralRegister} className="space-y-4">
                    <div>
                      <label htmlFor="referralEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="referralEmail"
                        value={referralEmail}
                        onChange={(e) => setReferralEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!referralEmail.trim()}
                      className="w-full py-3 px-4 bg-[#1086a4] hover:bg-[#0c6d85] disabled:bg-gray-400 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                    >
                      Join Now
                    </button>
                  </form>

                  <p className="mt-6 text-center text-sm text-gray-500">
                    Already have an account?{' '}
                    <Link to="/login" className="text-[#1086a4] font-semibold hover:text-[#054581] transition-colors">
                      Sign in
                    </Link>
                  </p>
                </div>
              )}
            </>
          ) : isProgramLink ? (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-[#1086a4] rounded-full animate-spin mb-4" />
                  <h2 className="text-2xl font-bold text-[#054581] mb-2">Setting up your account...</h2>
                  <p className="text-gray-500 text-sm">Loading, please wait.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-[#054581] mb-2">Join the Program</h2>
                    <p className="text-gray-500 text-sm">Enter your email to get started. Your login password will be sent to you.</p>
                  </div>

                  {msg && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {msg}
                    </div>
                  )}

                  <form onSubmit={handleProgramRegister} className="space-y-4">
                    <div>
                      <label htmlFor="programEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="programEmail"
                        value={referralEmail}
                        onChange={(e) => setReferralEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!referralEmail.trim()}
                      className="w-full py-3 px-4 bg-[#1086a4] hover:bg-[#0c6d85] disabled:bg-gray-400 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                    >
                      Join Now
                    </button>
                  </form>

                  <p className="mt-6 text-center text-sm text-gray-500">
                    Already have an account?{' '}
                    <Link to="/login" className="text-[#1086a4] font-semibold hover:text-[#054581] transition-colors">
                      Sign in
                    </Link>
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Header + step indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold text-[#054581]">Create account</h2>
                    <p className="text-gray-500 mt-1">Step {getDisplayStep()} of {totalSteps}</p>
                  </div>
                  <div className="flex gap-1.5">
                    {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
                      <div
                        key={step}
                        className={`h-2 w-8 rounded-full transition-colors duration-300 ${
                          step <= getDisplayStep() ? 'bg-[#1086a4]' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {renderStep()}

              {currentStep === 1 && (
                <p className="mt-6 text-center text-sm text-gray-500">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#1086a4] font-semibold hover:text-[#054581] transition-colors">
                    Sign in
                  </Link>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  error: state.error,
  registrationData: state.auth.registrationData,
  registrationTerms: state.termsConditions.registrationTerms,
  termsFetched: state.termsConditions.termsFetched
});

export default connect(mapStateToProps, {
  register, verifyEmail, verifyGroupCode, getTermsByProgram, clearErrors, clearRegistrationData, selfRegister, logout
})(RegisterPage);
